// 화면 기준 cellPx 단위 그리드로 항목들을 묶는다. 현재 줌 레벨이 화면 픽셀당
// 얼마의 위경도 폭을 차지하는지를 SDK가 알려주는 bounds + size로 환산해 cell 크기
// (위경도 단위)를 결정한다. carematch의 clusterByGrid와 같은 알고리즘.
//
// 같은 셀에 1개만 들어가면 single 그룹으로 — 호출자가 개별 마커 키를 재사용해
// 클러스터링 토글이나 줌 변화에서도 같은 마커 객체를 유지할 수 있게 한다.

interface Clusterable {
  id: number;
  lat: number;
  lng: number;
}

export interface ClusterGroup<T extends Clusterable> {
  // diff에 사용할 고유 키. count=1이면 "kind:single:id", 그 외 "kind:cell:cx_cy".
  // count=1 → single 키를 부여해 클러스터링 ON/OFF 사이 같은 마커가 재사용된다.
  key: string;
  lat: number;
  lng: number;
  items: T[];
}

// kindPrefix는 종을 분리해 키 충돌을 막기 위함 ("elderly" / "manager" / "org").
export function clusterByGrid<T extends Clusterable>(
  items: T[],
  map: any,
  cellPx: number,
  kindPrefix: string,
): ClusterGroup<T>[] {
  if (items.length === 0) return [];

  let cellDegLng = 0;
  let cellDegLat = 0;
  // viewport 컬링 경계(여유 마진 포함). 유효하면 화면 밖 항목을 클러스터링 전에 버린다.
  let cull: { minLat: number; maxLat: number; minLng: number; maxLng: number } | null = null;

  try {
    const bounds = map.getBounds();
    const size = (typeof map.getSize === "function" ? map.getSize() : null) ?? { width: 0, height: 0 };
    const ne = bounds.getNE();
    const sw = bounds.getSW();
    const lngSpan = ne.lng() - sw.lng();
    const latSpan = ne.lat() - sw.lat();
    const pxW = size.width  || 1;
    const pxH = size.height || 1;
    cellDegLng = (lngSpan / pxW) * cellPx;
    cellDegLat = (latSpan / pxH) * cellPx;
    // 화면 밖 마커는 그릴 필요가 없다 — 전국 수천 개를 확대해도 보이는 영역만 처리해
    // 마커 객체 수를 viewport 규모로 묶어둔다. 가장자리 pop-in을 줄이려 한 화면의 50%를
    // 사방 마진으로 더한다(팬할 때 미리 그려져 있게).
    if (lngSpan > 0 && latSpan > 0) {
      const mLat = latSpan * 0.5;
      const mLng = lngSpan * 0.5;
      cull = {
        minLat: sw.lat() - mLat, maxLat: ne.lat() + mLat,
        minLng: sw.lng() - mLng, maxLng: ne.lng() + mLng,
      };
    }
  } catch {
    // 줌이 깊거나 size를 못 읽으면 모두 single 폴백(컬링도 생략).
  }

  // 컬링 적용 — 경계가 유효할 때만. 경계 밖 항목은 제외.
  const inView = cull
    ? items.filter((it) =>
        it.lat >= cull!.minLat && it.lat <= cull!.maxLat &&
        it.lng >= cull!.minLng && it.lng <= cull!.maxLng)
    : items;

  if (cellDegLng <= 0 || cellDegLat <= 0) {
    return inView.map((it) => ({
      key:   `${kindPrefix}:single:${it.id}`,
      lat:   it.lat,
      lng:   it.lng,
      items: [it],
    }));
  }

  const cells = new Map<string, T[]>();
  for (const it of inView) {
    const cx = Math.floor(it.lng / cellDegLng);
    const cy = Math.floor(it.lat / cellDegLat);
    const k  = `${cx}_${cy}`;
    let arr = cells.get(k);
    if (!arr) { arr = []; cells.set(k, arr); }
    arr.push(it);
  }

  const groups: ClusterGroup<T>[] = [];
  for (const [cellKey, arr] of cells) {
    if (arr.length === 1) {
      const it = arr[0];
      groups.push({
        key:   `${kindPrefix}:single:${it.id}`,
        lat:   it.lat,
        lng:   it.lng,
        items: [it],
      });
    } else {
      // 셀 중심 = 평균 좌표. 셀 경계 좌표를 쓰면 마커가 화면 밖에 박힐 위험.
      let sLat = 0;
      let sLng = 0;
      for (const it of arr) { sLat += it.lat; sLng += it.lng; }
      groups.push({
        key:   `${kindPrefix}:cell:${cellKey}`,
        lat:   sLat / arr.length,
        lng:   sLng / arr.length,
        items: arr,
      });
    }
  }
  return groups;
}
